// Fiche d'un site surveillé (#/websites/{id}) : état, disponibilité par période
// (calculée au temps, sur les incidents), temps de réponse, incidents, et la
// configuration en lecture.

import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  Pause,
  Play,
  Pencil,
  Trash2,
  Mail,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  Globe,
} from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useDashboard } from '../views/dashboardContext.jsx';
import { fmtRelativeFromNow } from '../lib/format.jsx';
import { ResponseTimeChart } from './ResponseTimeChart.jsx';
import { WebMonitorModal } from './WebMonitorModal.jsx';
import {
  wmCall,
  monitorState,
  StatusDot,
  fmtPct,
  pctTone,
  fmtDuration,
  fmtInterval,
  certDaysLeft,
  certTone,
  fmtDateTime,
} from '../lib/webmonitor.jsx';

const card = 'bg-card border border-rule rounded-2xl px-5 py-4';
const btn =
  'bg-rule border border-rule-3 rounded-lg px-3 py-1.5 text-fg-2 cursor-pointer text-xs font-semibold hover:bg-rule-2 disabled:opacity-50 inline-flex items-center gap-1.5';
const PERIODS = [
  [1, '1 h'],
  [24, '24 h'],
  [168, '7 j'],
  [720, '30 j'],
];
const PERIOD_LABEL = { 1: '24 heures', 7: '7 jours', 30: '30 jours', 90: '90 jours' };

function KV({ k, children }) {
  return (
    <>
      <span className="text-muted-2">{k}</span>
      <span className="text-fg-2 min-w-0 break-words">{children}</span>
    </>
  );
}

export function WebMonitorDetail({ id }) {
  const { can } = useDashboard();
  const canManage = can('ManageProvisioning');
  const [m, setM] = useState(null);
  const [missing, setMissing] = useState(false);
  const [hours, setHours] = useState(24);
  const [series, setSeries] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [moreIncidents, setMoreIncidents] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    const r = await wmCall('/' + id);
    if (r.status === 404) setMissing(true);
    else if (r.ok) setM(r.json);
  }, [id]);
  const loadSeries = useCallback(async () => {
    setSeries(await api.get(`/platform/web-monitors/${id}/series?hours=${hours}`));
  }, [id, hours]);
  const loadIncidents = useCallback(async () => {
    const r = (await api.get(`/platform/web-monitors/${id}/incidents?limit=20`)) || [];
    setIncidents(r);
    setMoreIncidents(r.length === 20);
  }, [id]);

  useEffect(() => {
    load();
    loadIncidents();
  }, [load, loadIncidents]);
  useEffect(() => {
    setSeries(null);
    loadSeries();
  }, [loadSeries]);
  useEffect(() => {
    const iv = setInterval(() => {
      load();
      loadSeries();
      loadIncidents();
    }, 30000);
    return () => clearInterval(iv);
  }, [load, loadSeries, loadIncidents]);

  const back = () => (window.location.hash = '#/websites');
  const refreshAll = () => {
    load();
    loadSeries();
    loadIncidents();
  };

  const act = async (key, path, method = 'POST') => {
    setBusy(key);
    setNotice(null);
    const r = await wmCall(path, method);
    setBusy(null);
    return r;
  };
  const checkNow = async () => {
    const r = await act('check', `/${id}/check`);
    if (r.ok)
      setNotice(
        r.json.up
          ? { ok: true, text: `Le site répond : HTTP ${r.json.statusCode} en ${r.json.responseMs} ms.` }
          : { ok: false, text: r.json.error }
      );
    refreshAll();
  };
  const testNotification = async () => {
    const r = await act('notify', `/${id}/test-notification`);
    setNotice(
      r.ok
        ? { ok: true, text: 'Courriel de test envoyé à ' + r.json.sentTo.join(', ') + '.' }
        : { ok: false, text: r.json?.error || 'Échec (erreur ' + r.status + ').' }
    );
  };
  const togglePause = async () => {
    await act('pause', `/${id}/${m.enabled ? 'pause' : 'resume'}`);
    refreshAll();
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer la surveillance de « ${m.name} » ?\n\nSon historique (vérifications et incidents) est effacé.`)) return;
    const r = await act('delete', `/${id}`, 'DELETE');
    if (r.ok) back();
  };
  const loadMore = async () => {
    const last = incidents[incidents.length - 1];
    const r = (await api.get(`/platform/web-monitors/${id}/incidents?limit=20&before=${last.id}`)) || [];
    setIncidents([...incidents, ...r]);
    setMoreIncidents(r.length === 20);
  };

  if (missing)
    return (
      <div className="w-full">
        <button onClick={back} className="bg-transparent border-none text-muted hover:text-fg cursor-pointer text-xs inline-flex items-center gap-1 p-0 mb-4">
          <ArrowLeft size={14} /> Sites web
        </button>
        <div className={`${card} text-muted-2 text-sm`}>Ce site n'existe plus, ou vous n'y avez pas accès.</div>
      </div>
    );
  if (!m) return <div className="text-muted-2 text-xs p-8">Chargement…</div>;

  const s = monitorState(m);
  const days = certDaysLeft(m);
  const openIncident = incidents?.find((i) => !i.endedAt);

  return (
    <div className="w-full">
      <button onClick={back} className="bg-transparent border-none text-muted hover:text-fg cursor-pointer text-xs inline-flex items-center gap-1 p-0 mb-3">
        <ArrowLeft size={14} /> Sites web
      </button>

      {/* ─── En-tête ─── */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold text-fg inline-flex items-center gap-2.5">
            <StatusDot m={m} size="w-3 h-3" />
            <span className="truncate">{m.name}</span>
          </h1>
          <div className="text-xs text-muted-2 mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-muted">{m.method}</span>
            <a href={m.url} target="_blank" rel="noreferrer noopener" className="font-mono text-blue-400 hover:underline inline-flex items-center gap-1 break-all">
              {m.url} <ExternalLink size={11} />
            </a>
            {(m.clientName || m.agentName) && <span>·</span>}
            {m.clientName && <span>{m.clientName}</span>}
            {m.agentName && (
              <a href={'#/devices/' + m.agentId} className="text-blue-400 hover:underline">
                {m.agentName}
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className={btn} onClick={checkNow} disabled={!!busy}>
            {busy === 'check' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Vérifier maintenant
          </button>
          {canManage && (
            <>
              <button className={btn} onClick={testNotification} disabled={!!busy} title="Envoie un courriel de test aux personnes prévenues">
                {busy === 'notify' ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Tester la notification
              </button>
              <button className={btn} onClick={togglePause} disabled={!!busy}>
                {m.enabled ? <Pause size={13} /> : <Play size={13} />} {m.enabled ? 'Pause' : 'Reprendre'}
              </button>
              <button className={btn} onClick={() => setEditing(true)} disabled={!!busy}>
                <Pencil size={13} /> Modifier
              </button>
              <button
                className={`${btn} hover:!bg-crit/15 hover:!text-crit`}
                onClick={remove}
                disabled={!!busy}
                title="Supprimer la surveillance"
                aria-label="Supprimer"
              >
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {notice && (
        <div
          className={`mb-4 rounded-xl px-3.5 py-2.5 text-[12px] text-fg-2 border ${
            notice.ok ? 'bg-ok/10 border-ok/30' : 'bg-crit/10 border-crit/30'
          }`}
        >
          {notice.text}
        </div>
      )}

      {s.key === 'down' && (
        <div className="mb-4 bg-crit/10 border border-crit/30 rounded-xl px-4 py-3 text-[12.5px] text-fg-2 flex gap-2.5">
          <AlertTriangle size={16} strokeWidth={1.75} className="text-crit flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-crit">En panne depuis {fmtRelativeFromNow(m.statusSince)}</div>
            <div>{m.lastError}</div>
            {openIncident && <div className="text-[11px] text-muted-2 mt-0.5">Premier échec : {fmtDateTime(openIncident.startedAt)}</div>}
          </div>
        </div>
      )}

      {/* ─── Synthèse ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <div className={card}>
          <div className="text-[11.5px] text-muted-2 mb-1.5">État</div>
          <div className={`text-lg font-bold ${s.tone}`}>{s.label}</div>
          <div className="text-[11.5px] text-muted-2 mt-0.5">
            {s.since ? 'depuis ' + fmtRelativeFromNow(s.since) : s.key === 'paused' ? 'aucune vérification' : 'première vérification en cours'}
          </div>
        </div>
        <div className={card}>
          <div className="text-[11.5px] text-muted-2 mb-1.5">Dernière vérification</div>
          <div className="text-lg font-bold text-fg">{m.lastCheckAt ? 'il y a ' + fmtRelativeFromNow(m.lastCheckAt) : '—'}</div>
          <div className="text-[11.5px] text-muted-2 mt-0.5">
            {m.lastStatusCode ? `HTTP ${m.lastStatusCode}` : 'pas de réponse'}
            {m.lastResponseMs != null && ` · ${m.lastResponseMs} ms`} · toutes les {fmtInterval(m.intervalSeconds)}
          </div>
        </div>
        <div className={card}>
          <div className="text-[11.5px] text-muted-2 mb-1.5 flex items-center gap-1">
            <ShieldCheck size={12} /> Certificat TLS
          </div>
          {days == null ? (
            <div className="text-lg font-bold text-muted-2">{m.url.startsWith('http://') ? 'Sans HTTPS' : '—'}</div>
          ) : (
            <>
              <div className={`text-lg font-bold ${certTone(m)}`}>{days < 0 ? 'Expiré' : `${days} jours`}</div>
              <div className="text-[11.5px] text-muted-2 mt-0.5 truncate" title={m.certSubject}>
                jusqu'au {fmtDateTime(m.certExpiresAt)}
                {m.certIssuer && ' · ' + m.certIssuer}
              </div>
            </>
          )}
        </div>
        <div className={card}>
          <div className="text-[11.5px] text-muted-2 mb-1.5">Disponibilité</div>
          <div className="grid grid-cols-[auto_auto_1fr] gap-x-3 gap-y-0.5 text-[12px] items-baseline">
            {m.uptime.map((u) => (
              <div key={u.days} className="contents">
                <span className="text-muted-2">{PERIOD_LABEL[u.days] || u.days + ' j'}</span>
                <span className={`font-mono font-semibold ${pctTone(u.percent)}`}>{fmtPct(u.percent)}</span>
                <span className="text-[10.5px] text-muted-2 truncate">
                  {u.incidents === 0 ? '' : `${u.incidents} incident${u.incidents > 1 ? 's' : ''}, ${fmtDuration(u.downSeconds)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Temps de réponse ─── */}
      <div className={`${card} mb-4`}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-baseline gap-4 flex-wrap">
            <h2 className="text-sm font-bold text-fg">Temps de réponse</h2>
            {series?.avg != null && (
              <span className="text-[11.5px] text-muted-2">
                moy. <span className="font-mono text-fg-2">{series.avg} ms</span> · min.{' '}
                <span className="font-mono text-fg-2">{series.min} ms</span> · max.{' '}
                <span className="font-mono text-fg-2">{series.max} ms</span>
                {series.failures > 0 && (
                  <span className="text-crit">
                    {' '}
                    · {series.failures} échec{series.failures > 1 ? 's' : ''} sur {series.checks}
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            {PERIODS.map(([h, l]) => (
              <button
                key={h}
                onClick={() => setHours(h)}
                className={`rounded-md px-2.5 py-0.5 text-[11px] cursor-pointer border ${
                  hours === h ? 'bg-blue-500/15 border-blue-500 text-blue-400 font-semibold' : 'bg-card border-rule-2 text-muted hover:text-fg-2'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ResponseTimeChart series={series} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4">
        {/* ─── Incidents ─── */}
        <div className={`${card} !px-0 !pb-1`}>
          <h2 className="text-sm font-bold text-fg px-5 mb-2">Incidents</h2>
          {incidents === null ? (
            <div className="px-5 pb-4 text-muted-2 text-xs">Chargement…</div>
          ) : incidents.length === 0 ? (
            <div className="px-5 pb-4 text-muted-2 text-xs">Aucune panne enregistrée.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr className="text-left text-muted-2 text-[11px] uppercase">
                    <th className="px-5 py-1.5 font-medium">Début</th>
                    <th className="px-3 py-1.5 font-medium">Durée</th>
                    <th className="px-3 py-1.5 font-medium">Cause</th>
                    <th className="px-5 py-1.5 font-medium text-right">Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((i) => (
                    <tr key={i.id} className="border-t border-rule">
                      <td className="px-5 py-2 whitespace-nowrap text-fg-2">{fmtDateTime(i.startedAt)}</td>
                      <td className={`px-3 py-2 whitespace-nowrap font-mono ${i.endedAt ? 'text-fg-2' : 'text-crit'}`}>
                        {fmtDuration(i.durationSeconds)}
                      </td>
                      <td className="px-3 py-2 text-fg-2">
                        {i.statusCode && (
                          <span className="font-mono text-[10.5px] font-semibold bg-crit/15 text-crit rounded px-1 py-0.5 mr-1.5">
                            {i.statusCode}
                          </span>
                        )}
                        {i.cause}
                      </td>
                      <td className="px-5 py-2 whitespace-nowrap text-right">
                        {!i.endedAt ? (
                          <span className="text-crit font-semibold">En cours</span>
                        ) : i.endReason === 'paused' ? (
                          <span className="text-muted-2" title={fmtDateTime(i.endedAt)}>
                            Mise en pause
                          </span>
                        ) : (
                          <span className="text-ok" title={fmtDateTime(i.endedAt)}>
                            Rétabli
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {moreIncidents && (
                <div className="px-5 py-2">
                  <button onClick={loadMore} className="bg-transparent border-none text-blue-400 cursor-pointer text-[11.5px] font-semibold p-0">
                    Incidents plus anciens…
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── Configuration ─── */}
        <div className={card}>
          <h2 className="text-sm font-bold text-fg mb-2.5 flex items-center gap-1.5">
            <Globe size={14} /> Surveillance
          </h2>
          <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-[12px]">
            <KV k="Prévenus">
              {m.notifyUsers.length === 0 && !m.notifyEmails ? (
                <span className="text-warn">personne</span>
              ) : (
                <span>
                  {[...m.notifyUsers.map((u) => u.name), ...(m.notifyEmails ? m.notifyEmails.split(/,\s*/) : [])].join(', ')}
                </span>
              )}
            </KV>
            <KV k="Intervalle">{fmtInterval(m.intervalSeconds)}</KV>
            <KV k="Panne déclarée">
              après {m.confirmationCount} échec{m.confirmationCount > 1 ? 's' : ''} de suite
            </KV>
            <KV k="Codes acceptés">
              <span className="font-mono">{m.expectedStatusCodes}</span>
            </KV>
            <KV k="Délai d'attente">{m.timeoutSeconds} s</KV>
            {m.keyword && (
              <KV k="Mot-clé">
                « {m.keyword} » {m.keywordMustBeAbsent ? 'absent' : 'présent'}
                {m.keywordCaseSensitive && ' (casse respectée)'}
              </KV>
            )}
            <KV k="Redirections">{m.followRedirects ? 'suivies' : 'non suivies'}</KV>
            <KV k="Certificat">
              {m.verifyTls ? 'doit être valide' : 'non vérifié'}
              {m.certWarningDays > 0 ? `, avertir ${m.certWarningDays} j avant` : ''}
            </KV>
            {m.headers.length > 0 && (
              <KV k="En-têtes">
                <span className="font-mono text-[11px]">{m.headers.map((h) => h.name).join(', ')}</span>
              </KV>
            )}
            {m.basicAuthUser && <KV k="Authentification">{m.basicAuthUser}</KV>}
            <KV k="Ajouté">
              {fmtDateTime(m.createdAt)}
              {m.createdBy && <span className="text-muted-2"> par {m.createdBy}</span>}
            </KV>
          </div>
        </div>
      </div>

      {editing && (
        <WebMonitorModal
          monitor={m}
          onClose={(saved) => {
            setEditing(false);
            if (saved) refreshAll();
          }}
        />
      )}
    </div>
  );
}
